import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { VerifiedUser, CheckCircle } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { aiFeatureService, AiFeatureStatus } from "../services/aiFeatureService";
import { colors, typography } from "../theme/designSystem";
import { formatDateTime } from "../utils/datetime";
import AiIntegrationConfig from "./AiIntegrationConfig";
import VoiceTranscriptionConfig from "./VoiceTranscriptionConfig";
import PatientAssistantConfig from "./PatientAssistantConfig";
import AssistantAreasConfig from "./AssistantAreasConfig";
import AssistantMemoriesConfig from "./AssistantMemoriesConfig";

// Aceite do termo de uso dos Assistentes de IA (M0). Só o admin da entidade acessa (o backend
// valida via EntityOwnershipGuard). Habilitar/usar a IA exige aceite vigente da versão corrente.
const AiFeatureTerms: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName = params.get("entityName") || user?.entityName || "Entidade";

  const [status, setStatus] = useState<AiFeatureStatus | null>(null);
  // Começa carregando: com false, o primeiro paint (antes do efeito) mostrava a mensagem de erro.
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const load = async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await aiFeatureService.getStatus(entityId);
      setStatus(data);
    } catch (error) {
      console.error("Erro ao carregar o termo de IA:", error);
      showToast("Erro ao carregar o termo de uso dos Assistentes de IA.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${entityName} / Configuração de IA`;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccept = async () => {
    if (saving || !agreed) return;
    setSaving(true);
    try {
      await aiFeatureService.acceptTerms(entityId);
      showToast("Termo aceito com sucesso.", "success");
      setAgreed(false);
      await load();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response?.data
          ?.description || "Erro ao registrar o aceite do termo.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <div className="c4u-page-header c4u-page-header--card">
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Configuração de IA</h1>
              <p className="c4u-page-header__subtitle">Aceite do termo de uso — necessário para habilitar os assistentes de IA nesta entidade.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "1.5rem",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              border: "1px solid #e9ecef",
            }}
          >
            <SavingOverlay open={saving} message="Registrando aceite..." />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : !status ? (
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Não foi possível carregar o termo.
              </Typography>
            ) : !status.enabled ? (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                  Feature não habilitada
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Os Assistentes de IA ainda não foram habilitados para esta entidade. Entre em
                  contato com o suporte da Clinic4Us para ativar a funcionalidade.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  <VerifiedUser sx={{ color: colors.primary }} />
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "1.05rem", fontWeight: 600, color: colors.textPrimary }}
                  >
                    {status.termsTitle}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, ml: "auto" }}>
                    Versão {status.currentTermsVersion}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontSize: typography.fontSize.sm,
                    color: colors.textPrimary,
                    backgroundColor: colors.background,
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    p: "1rem",
                    maxHeight: 340,
                    overflowY: "auto",
                    mb: 2,
                  }}
                >
                  {status.termsBody}
                </Box>

                {status.termsAccepted ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <CheckCircle sx={{ color: colors.success }} />
                      <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                        Termo versão <strong>{status.acceptedVersion || status.currentTermsVersion}</strong>
                        {status.acceptedByName ? (
                          <>
                            {" "}aceito por <strong>{status.acceptedByName}</strong>
                          </>
                        ) : (
                          " aceito"
                        )}{" "}
                        em <strong>{formatDateTime(status.acceptedAt)}</strong>.
                      </Typography>
                    </Box>
                    {entityId && <AiIntegrationConfig entityId={entityId} onToast={showToast} />}
                    <VoiceTranscriptionConfig onToast={showToast} />
                    {entityId && <AssistantAreasConfig entityId={entityId} onToast={showToast} />}
                    {entityId && <AssistantMemoriesConfig onToast={showToast} />}
                    {entityId && <PatientAssistantConfig entityId={entityId} onToast={showToast} />}
                  </>
                ) : (
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          sx={{ color: colors.primary, "&.Mui-checked": { color: colors.primary } }}
                        />
                      }
                      label="Li e aceito o termo de uso, em nome da entidade, assumindo a responsabilidade descrita."
                    />
                    <Box sx={{ mt: 2 }}>
                      <Button
                        onClick={handleAccept}
                        disabled={!agreed || saving}
                        variant="contained"
                        sx={{ textTransform: "none", backgroundColor: colors.primary }}
                      >
                        Aceitar termo
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default AiFeatureTerms;
